import { useCallback, useEffect, useRef, useState } from "react"


type UseSingleFlightRequestParams<TArgs, TResult> = {
  request: (args: TArgs) => Promise<TResult>
}

type UseSingleFlightRequestResult<TArgs, TResult> = {
  data: TResult | null
  error: string | null
  loading: boolean
  run: (args: TArgs) => void
}

export default function useSingleFlightRequest<TArgs, TResult>({
  request,
}: UseSingleFlightRequestParams<TArgs, TResult>): UseSingleFlightRequestResult<TArgs, TResult> {
  const [data, setData] = useState<TResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const isMountedRef = useRef(true)
  const isLoadingRef = useRef(false)
  const pendingArgsRef = useRef<TArgs | null>(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const execute = useCallback(async (args: TArgs) => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    isLoadingRef.current = true

    if (isMountedRef.current) {
      setLoading(true)
      setError(null)
    }

    try {
      const result = await request(args)

      if (!isMountedRef.current) return

      const hasPending = pendingArgsRef.current !== null
      const isLatest = requestId === requestIdRef.current

      if (!hasPending && isLatest) {
        setData(result)
      }
    } catch (err) {
      if (!isMountedRef.current) return

      const hasPending = pendingArgsRef.current !== null
      const isLatest = requestId === requestIdRef.current

      if (!hasPending && isLatest) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      }
    } finally {
      const nextArgs = pendingArgsRef.current

      if (nextArgs !== null) {
        pendingArgsRef.current = null
        await execute(nextArgs)
      } else {
        isLoadingRef.current = false

        if (isMountedRef.current) {
          setLoading(false)
        }
      }
    }
  }, [request])

  const run = useCallback((args: TArgs) => {
    if (isLoadingRef.current) {
      pendingArgsRef.current = args
      return
    }

    void execute(args)
  }, [execute])

  return {
    data,
    error,
    loading,
    run,
  }
}
